import { searchAdminAgreement } from '@api/generated/sdk.gen';
import { designTokens } from '@app/theme';
import { formatTiyinAsSum } from '@shared/lib/money';
import { useQuery } from '@tanstack/react-query';
import { Card, Empty, Grid, Input, Pagination, Select, Space, Spin, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type {
  AgreementSummaryResponse,
  SearchAdminAgreementData,
} from '@api/generated/types.gen';

import { AGREEMENT_STATUS_COLORS } from './lib';

const { Title, Text } = Typography;

type StatusFilter = NonNullable<NonNullable<SearchAdminAgreementData['query']>['status']>;

const STATUS_OPTIONS: StatusFilter[] = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'TERMS_LOCKED',
  'SIGNED',
  'AWAITING_FEE',
  'AWAITING_DISBURSEMENT',
  'NOT_DISBURSED',
  'ACTIVE',
  'REPAYING',
  'OVERDUE',
  'DISPUTE',
  'ENFORCEMENT',
  'CLOSED',
  'CANCELLED',
];

/**
 * Список договоров платформы (`GET /api/admin/agreements`) для поддержки:
 * поиск по подстроке телефона контрагента и фильтр по статусу, серверная
 * пагинация. Клик по номеру договора ведёт в карточку с вкладкой «Действия».
 * Требует право `agreement.read.all` (гейтится роутом).
 */
export function AgreementsListPage() {
  const { t } = useTranslation(['agreements', 'common']);
  const screens = Grid.useBreakpoint();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const listQuery = useQuery({
    queryKey: ['admin-agreements', q, status ?? '', currentPage],
    queryFn: async () => {
      const res = await searchAdminAgreement({
        query: {
          page: currentPage - 1,
          size: pageSize,
          ...(q !== '' && { q }),
          ...(status !== undefined && { status }),
        },
      });
      return res.data ?? {};
    },
  });

  const items: AgreementSummaryResponse[] = listQuery.data?.content ?? [];
  const total = listQuery.data?.totalElements ?? 0;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
          {t('agreements:title')}
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          {t('agreements:subtitle')}
        </Text>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Input.Search
          allowClear
          placeholder={t('agreements:searchPlaceholder')}
          onSearch={(value) => {
            setQ(value.trim());
            setCurrentPage(1);
          }}
          style={{ width: '100%', maxWidth: 320 }}
        />
        <Select<StatusFilter>
          allowClear
          placeholder={t('agreements:statusFilter')}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setCurrentPage(1);
          }}
          options={STATUS_OPTIONS.map((s) => ({
            value: s,
            label: t(`agreements:statuses.${s}`, { defaultValue: s }),
          }))}
          style={{ width: '100%', maxWidth: 220 }}
        />
      </div>

      <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
        <Spin spinning={listQuery.isLoading}>
          {!listQuery.isLoading && items.length === 0 ? (
            <div style={{ padding: 32 }}>
              <Empty description={t('agreements:empty')} />
            </div>
          ) : (
            items.map((a, idx) => (
              <div
                key={a.id}
                style={{
                  padding: '12px 16px',
                  borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Link
                    to={`/agreements/${a.id ?? ''}`}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 14,
                      fontWeight: 600,
                      color: designTokens.navy900,
                      borderBottom: `1px dashed ${designTokens.slate300}`,
                    }}
                  >
                    {a.reference}
                  </Link>
                  <Tag
                    color={AGREEMENT_STATUS_COLORS[a.status ?? ''] ?? 'default'}
                    style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                  >
                    {t(`agreements:statuses.${a.status ?? ''}`, { defaultValue: a.status })}
                  </Tag>
                </div>
                <div
                  style={{
                    marginBlockStart: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px 16px',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`agreements:types.${a.agreementType ?? 'LOAN'}`, {
                      defaultValue: a.agreementType,
                    })}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {t('agreements:amount', { amount: formatTiyinAsSum(a.amountTiyin) })}
                  </Text>
                  {a.counterpartyPhoneMasked && (
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace' }}
                    >
                      {a.counterpartyPhoneMasked}
                    </Text>
                  )}
                </div>
              </div>
            ))
          )}

          {total > pageSize && (
            <div
              style={{
                padding: '12px 16px',
                borderBlockStart: `1px solid ${designTokens.slate100}`,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={(p) => {
                  setCurrentPage(p);
                }}
                simple={!screens.md}
                showSizeChanger={false}
              />
            </div>
          )}
        </Spin>
      </Card>
    </Space>
  );
}
